// src/services/chatService.js
import { configuration } from '../modules/configuration';
import ActionCable from 'actioncable';
import { logger } from '@/utils/logger';
import store from '@/store';

export class ChatService {
  constructor() {
    this.cable = null;
    this.subscriptions = new Map();
    this.messageCallbacks = new Map();
    this.currentUserSessionId = null;
  }

  connect() {
    // console.log('Starting ActionCable connection...');
    
    try {
      const baseUrl = configuration.baseUrl;
      // console.log('Base URL from config:', baseUrl);

      if (!baseUrl) {
        throw new Error('Base URL is not configured');
      }

      const cleanBaseUrl = baseUrl.replace(/\/$/, '');
      const host = cleanBaseUrl.replace(/^https?:\/\//, '');
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsURL = `${wsProtocol}//${host}/cable`;
      
      // console.log('Constructed WebSocket URL:', wsURL);
      this.cable = ActionCable.createConsumer(wsURL);
      // console.log('ActionCable consumer created');
    } catch (error) {
      console.error('Failed to create ActionCable consumer:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  }

  subscribe(callbacks = {}, userSessionId = null) {
    // console.log("Creating channel subscription with userSessionId:", userSessionId);
    if (!this.cable) {
      throw new Error("Cable not initialized");
    }

    this.currentUserSessionId = userSessionId;

    // Remove the chat_ prefix - let backend handle it
    const channelParams = { 
      channel: "ChatChannel",
      room: userSessionId, // Just pass the session ID, backend will add chat_ prefix
      user_session_id: userSessionId
    };

    // console.log('Subscribing with params:', channelParams);

    const subscription = this.cable.subscriptions.create(
      channelParams,
      {
        connected: () => {
          // console.log(`Connected to chat channel for session: ${userSessionId}`);
          if (callbacks.onConnected) callbacks.onConnected();
        },
        disconnected: () => {
          // console.log(`Disconnected from chat channel for session: ${userSessionId}`);
          if (callbacks.onDisconnected) callbacks.onDisconnected();
        },
        received: (data) => {
          // console.log(`Raw message received for session ${userSessionId}:`, data);
          // console.log('Message structure:', JSON.stringify(data, null, 2));
          this.handleReceivedMessage(data, callbacks);
        },
        rejected: () => {
          // console.error(`Subscription rejected for session: ${userSessionId}`);
          if (callbacks.onRejected) callbacks.onRejected();
        }
      }
    );

    this.subscriptions.set(userSessionId, {
      subscription,
      params: channelParams
    });

    // console.log('Subscription created');
    return subscription;
  }

  handleReceivedMessage(data, callbacks) {
    try {
      logger.log('Processing received message:', data);

      // Check for message handler first
      const handler = this.messageCallbacks.get(this.currentUserSessionId);
      if (handler) {
        logger.log('Found message handler, executing...');
        handler(data);
      }

      // Process based on message type
      if (data.event === 'chat_started') {
        logger.log('Chat started event received');
        if (callbacks.onChatStarted) callbacks.onChatStarted(data);
        return;
      }

      // Check for both status and response.status
      const status = data.status || (data.response && data.response.status);
      
      if (status === 'completed' || (data.response && data.response.title)) {
        logger.log('Chat completed, updating cart...');
        store.dispatch("fetchCartQuantityData").then(() => {
          logger.log('Cart updated successfully');
        }).catch(error => {
          logger.error('Error updating cart:', error);
        });
        if (callbacks.onChatCompleted) callbacks.onChatCompleted(data);
        return;
      }

      if (status === 'error' || data.error) {
        logger.error('Chat error received:', data.error);
        
        // Create error popup content
        if (data.popup) { 
          store.dispatch('hidePopup');
          setTimeout(() => {
            store.dispatch('showPopup', data.popup);
          }, 0);
        }
        
        if (callbacks.onError) callbacks.onError(data.error);
        return;
      }

      // Handle any other messages
      if (callbacks.onReceived) {
        logger.log('Passing message to general handler');
        callbacks.onReceived(data);
      }
    } catch (error) {
      logger.error('Error handling received message:', error);
      
      // Create error popup content for unexpected errors
      if (data.popup) { 
        store.dispatch('hidePopup');
        setTimeout(() => {
          store.dispatch('showPopup', data.popup);
        }, 0);
      }
      
    
      if (callbacks.onError) callbacks.onError(error);
    }
  }

  async processChat(userSessionId, formData) {
    if (!userSessionId) {
      throw new Error('User session ID is required');
    }

    if (!this.subscriptions.has(userSessionId)) {
      await this.initializeConnection(userSessionId);
    }

    return new Promise((resolve, reject) => {
      try {
        const subscriptionData = this.subscriptions.get(userSessionId);
        if (!subscriptionData || !subscriptionData.subscription) {
          throw new Error('No active subscription');
        }

        const subscription = subscriptionData.subscription;

        // Set up response handling before sending the message
        const messageHandler = (data) => {
          if (data.event === 'chat_started') {
            return; // Don't resolve yet, wait for completion
          }

          const status = data.status || (data.response && data.response.status);
          
          if (status === 'completed' || (data.response && data.response.title)) {
            this.messageCallbacks.delete(userSessionId);
            resolve(data);
          } else if (status === 'error' || data.error) {
            this.messageCallbacks.delete(userSessionId);
            reject(new Error(data.error || 'Chat error occurred'));
          }
        };

        this.messageCallbacks.set(userSessionId, messageHandler);

        // Send the form data directly
        subscription.perform('process_chat', { form_data: formData });

        // 4 minute timeout
        setTimeout(() => {
          if (this.messageCallbacks.has(userSessionId)) {
            this.messageCallbacks.delete(userSessionId);
            reject(new Error('Chat message timeout - no response after 4 minutes'));
          }
        }, 240000);

      } catch (error) {
        this.messageCallbacks.delete(userSessionId);
        reject(error);
      }
    });
  }

  formatPayload(formData, userSessionId) {
    const payload = {
      conversation: {
        user_session_id: userSessionId
      }
    };

    if (formData) {
      for (let [key, value] of formData.entries()) {
        // console.log('Processing form field:', key, value);
        const matches = key.match(/^(\w+)(?:\[(\w+)\])?$/);
        if (matches) {
          const [, namespace, field] = matches;
          if (field) {
            payload[namespace] = payload[namespace] || {};
            payload[namespace][field] = value;
          } else {
            payload[namespace] = value;
          }
        }
      }
    }

    // console.log('Formatted payload:', payload);
    return payload;
  }

  async initializeConnection(userSessionId) {
    if (!this.cable) {
      this.connect();
    }

    return new Promise((resolve, reject) => {
      try {
        this.subscribe({
          onConnected: () => {
            // console.log('Successfully connected to chat channel');
            resolve();
          },
          onRejected: () => {
            // console.error('Subscription was rejected');
            reject(new Error('Subscription rejected'));
          },
          onError: (error) => {
            reject(error);
          }
        }, userSessionId);
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect() {
    this.messageCallbacks.clear();
    
    this.subscriptions.forEach((subscriptionData, userSessionId) => {
      logger.log(`Unsubscribing from channel: ${userSessionId}`);
      this.cable.subscriptions.remove(subscriptionData.subscription);
    });
    
    this.subscriptions.clear();
    
    if (this.cable) {
      this.cable = null;
    }

    this.currentUserSessionId = null;
  }
}

export const chatService = new ChatService();